/**
 * Working-UDA picker for a module's dark header bar.
 *
 * For modules that own no UDA scope of their own (Collections, Finance2, Legal Case
 * Management, Display Devices): it switches the SESSION's working UDA, exactly as the
 * top-bar selector in components/Header.tsx does (that selector is hidden there) —
 * selectTenant(uda_id, 'uda'), drop the cached server state, and announce
 * 'tenant-changed' so screens that fetch outside TanStack Query reload. The layouts key
 * their <Outlet /> on user.tenantId, so the page remounts in memory against the new UDA.
 *
 * Only state/regional users can switch; everyone else sees their UDA as a read-only chip.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { queryClient } from '@/lib/queryClient';
import ulbService from '@/modules/utility/shared/services/admin/ulbService';

interface UdaOption { uda_id: number; uda_nm: string; tenant_code: string; tenant_id: number; }

/** Same synthetic row as the top bar: uda_id 0 resolves to the state-level 'default' tenant. */
const ALL_UDAS_OPTION: UdaOption = { uda_id: 0, uda_nm: 'All UDAs', tenant_code: 'default', tenant_id: 0 };

export default function GlobalUdaHeaderSelect() {
  const { user, selectTenant } = useAuth();
  const isStateUser = (user as any)?.isStateRegionalUser || false;
  const [udas, setUdas] = useState<UdaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!isStateUser) return undefined;
    let cancelled = false;
    setLoading(true);
    ulbService.getAllUDAs()
      .then((list: any[]) => {
        if (cancelled) return;
        const opts: UdaOption[] = (list || [])
          .filter((u: any) => u?.tenant_id != null && u?.tenant_code != null)
          .map((u: any) => ({ uda_id: u.uda_id, uda_nm: u.uda_nm, tenant_code: String(u.tenant_code), tenant_id: Number(u.tenant_id) }));
        setUdas([ALL_UDAS_OPTION, ...opts]);
      })
      .catch(() => { if (!cancelled) setUdas([ALL_UDAS_OPTION]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isStateUser]);

  const current = udas.find((u) => u.tenant_code === user?.tenantId) || null;

  const handleChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const uda = udas.find((u) => String(u.uda_id) === e.target.value);
    if (!uda || uda.tenant_code === user?.tenantId) return;
    try {
      setSwitching(true);
      await selectTenant(uda.uda_id, 'uda');
      queryClient.clear();
      window.dispatchEvent(new CustomEvent('tenant-changed', { detail: { tenantId: uda.tenant_id, tenantCode: uda.tenant_code } }));
    } catch (error) {
      console.error('Error selecting UDA:', error);
    } finally {
      setSwitching(false);
    }
  }, [udas, user?.tenantId, selectTenant]);

  if (!isStateUser) {
    const own = (user as any)?.tenants?.find((t: any) => String(t?.code) === String(user?.tenantId))?.name;
    if (!own) return null;
    return (
      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-white/10 ring-1 ring-inset ring-white/20 text-white text-[11px] font-semibold" title="Your UDA">
        <i className="ri-building-2-line text-white/70"></i>
        <span className="truncate max-w-[160px]">{own}</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-white/70">UDA</span>
      <div className="relative">
        <select
          value={current ? String(current.uda_id) : ''}
          disabled={loading || switching}
          onChange={handleChange}
          title="Working UDA"
          className="appearance-none w-[170px] h-7 truncate rounded-md bg-white/10 ring-1 ring-inset ring-white/20 pl-2 pr-6 text-xs font-semibold text-white outline-none transition-colors hover:bg-white/20 focus:ring-white/50 disabled:cursor-not-allowed disabled:opacity-60 [&>option]:text-gray-900"
        >
          {!current && <option value="">{loading ? 'Loading UDAs...' : switching ? 'Switching...' : 'Select UDA'}</option>}
          {udas.map((u) => (
            <option key={u.uda_id} value={String(u.uda_id)}>{u.uda_nm}</option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-xs text-white/70"></i>
      </div>
    </div>
  );
}
